// folderContentAreaColumns.js
//
// Column definitions and filter configs for the FolderContentArea tables.
// Mirrors foldersTableConfig.tsx in the UI workspace, adapted for ZUI DataTable
// (TanStack ColumnDef shape) instead of the legacy Table component.
//
// View coverage:
//   buildAggregatedColumns       — All Types view  (documents + workflow items)
//   buildDocumentColumns         — Documents view
//   buildWorkflowColumns         — Workflows view  (issues, change controls, custom)
//   buildTaskColumns             — Tasks view
//   buildAggregatedFilterColumns — filters for All Types
//   buildDocumentFilterColumns   — filters for Documents
//   buildWorkflowFilterColumns   — filters for Workflows
//   buildTaskFilterColumns       — filters for Tasks
//
// Hidden-by-default columns are flagged via DOCUMENT_HIDDEN_COLUMNS /
// WORKFLOW_HIDDEN_COLUMNS; pass them as initialColumnVisibility to useTable.

import React from 'react';
import moment from 'moment';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Icon, Spinner, Tag, Tooltip } from '@zenqms/zui';
import { displayToast } from '../../app/app-slice';

// ─── FA class → ZUI icon name ─────────────────────────────────────────────────
// Mirrors toZuiIconName from foldersTableConfig.tsx in the UI workspace.
// Strips FA family tokens (fa, fas, far, …) and style tokens (solid, regular, …)
// from a class string like "fa-solid fa-diagram-project" and returns the last
// meaningful token ("diagram-project").

export function toZuiIconName(faIcon) {
  if (!faIcon) return undefined;
  const STYLE_TOKEN = /^(solid|regular|brands|light|thin|duotone)$/i;
  const FA_FAMILY_TOKEN = /^(fa|fas|far|fab|fal|fat|fad|fass|fasr|fasl|fast)$/i;
  const tokens = faIcon
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => t.replace(/^fa-/i, ''))
    .filter((t) => !FA_FAMILY_TOKEN.test(t))
    .filter((t) => !STYLE_TOKEN.test(t));
  return tokens.length > 0 ? tokens[tokens.length - 1] : undefined;
}

// ─── Item type ────────────────────────────────────────────────────────────────

/**
 * Derive a stable item type string from an audit_module value.
 *
 * Returns one of: 'document' | 'task' | 'workflow'
 *
 * This is the single source of truth for type branching. All cell renderers,
 * PDF URL routing, and API call selection use this instead of ad-hoc boolean
 * pairs (isWorkflow / isTask).
 */
export function resolveItemType(auditModule) {
  const mod = auditModule?.toLowerCase();
  if (!mod || mod === 'document') return 'document';
  if (mod === 'task') return 'task';
  return 'workflow';
}

// ─── Filename sanitization ────────────────────────────────────────────────────

/**
 * Strip characters that are problematic for common filesystems (Windows, macOS, Linux).
 * Collapses consecutive replacements and trims leading/trailing dots or spaces.
 */
function sanitizeFilename(name) {
  if (!name) return 'download';
  return (
    name
      // eslint-disable-next-line no-control-regex
      .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[.\s]+|[.\s]+$/g, '') || 'download'
  );
}

// ─── Status Tag (matches documents full table in UI workspace) ────────────────

const TAG_VARIANT_DOCUMENT_STATUS = {
  default: 'success',
  draft: 'warning',
  draft_review: 'warning',
  awaiting_approval: 'warning',
  rejected: 'warning',
  reviewed: 'warning',
  effective: 'success',
  approved: 'secondary',
  retired: 'error',
};

function StatusCell({ getValue }) {
  const value = getValue();
  if (!value) return null;
  const label = value
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
  return (
    <Tag type={TAG_VARIANT_DOCUMENT_STATUS[value.toLowerCase()] || TAG_VARIANT_DOCUMENT_STATUS.default} tagStyle="soft">
      {label}
    </Tag>
  );
}

// ─── Date cell ────────────────────────────────────────────────────────────────

function DateCell({ getValue }) {
  const v = getValue();
  if (!v) return <span>—</span>;
  // Normalise: stage_due_date may arrive as an array
  const raw = Array.isArray(v) ? v[0] : v;
  return <span>{moment.utc(raw).format('DD-MMM-YYYY')}</span>;
}

// ─── Content type cell ────────────────────────────────────────────────────────

function ContentTypeCell({ getValue }) {
  const raw = getValue();
  if (!raw) return null;
  const display =
    raw.toLowerCase() === 'scorm'
      ? 'SCORM'
      : raw
          .split(/[\s_-]+/)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');
  return <span>{display}</span>;
}

// ─── File extension uppercase cell ────────────────────────────────────────────

function UpperCaseCell({ getValue }) {
  const v = getValue();
  return <span>{v ? String(v).toUpperCase() : ''}</span>;
}

// ─── Module label resolution ──────────────────────────────────────────────────

const BUILTIN_MODULE_LABELS = {
  document: 'Documents',
  issue: 'Issues',
  change_control: 'Change Controls',
};

export function resolveModuleLabel(auditModule, facets = []) {
  if (BUILTIN_MODULE_LABELS[auditModule]) return BUILTIN_MODULE_LABELS[auditModule];
  const facet = facets.find((f) => f.audit_module === auditModule);
  return facet ? facet.plural_name : auditModule ?? '';
}

// ─── Clickable document/item link cell ───────────────────────────────────────

/**
 * Renders a clickable button that opens the PDF preview panel.
 *
 * @param {string}   value             — display text shown in the table cell
 * @param {string}   [panelTitle]      — title shown in the PDF preview panel header;
 *                                       defaults to `value` when not provided.
 *                                       Pass `"${key_id}: ${name}"` to get a consistent
 *                                       header regardless of which column was clicked.
 * @param {Function} onOpenDocument    — (id, name, itemType) => Promise
 * @param {string}   documentVersionId — the ID to pass to the PDF URL endpoint
 * @param {string}   [itemType]        — 'document' | 'task' | 'workflow' (default: 'document')
 */
export function DocumentLinkCell({ value, panelTitle, onOpenDocument, documentVersionId, itemType = 'document' }) {
  const [loading, setLoading] = React.useState(false);

  async function handleClick(e) {
    e.preventDefault();
    if (!documentVersionId || loading) return;
    setLoading(true);
    try {
      await onOpenDocument(documentVersionId, panelTitle ?? value, itemType);
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || !documentVersionId}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: loading ? 'wait' : documentVersionId ? 'pointer' : 'default',
        color: 'var(--zui-color-primary-400)',
        textDecoration: 'none',
        fontWeight: 'bold',
        textAlign: 'left',
        opacity: loading ? 0.6 : 1,
      }}
    >
      {value}
    </button>
  );
}

// ─── Download cell ────────────────────────────────────────────────────────────

/**
 * Renders a download button that fetches the PDF as a blob and triggers
 * a browser download with the item name as filename.
 *
 * Supports document, workflow, and task rows. For aggregated rows without a
 * corresponding id, returns null.
 */
function DownloadCell({ row, downloadContext }) {
  const [loading, setLoading] = React.useState(false);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { folderShareId, folderId } = downloadContext;
  const itemType = resolveItemType(row.original.audit_module);
  const documentVersionId = row.original.document_version_id;
  const workflowId = row.original.item_id;
  const itemName = row.original.name;
  const keyId = row.original.key_id;

  if (itemType === 'document' && !documentVersionId) return null;
  if (itemType === 'workflow' && !workflowId) return null;
  if (itemType === 'task' && !row.original.task_id) return null;

  async function handleDownload(event) {
    event.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      const pdfPath =
        itemType === 'document'
          ? `documents/${documentVersionId}/pdf`
          : itemType === 'task'
          ? `tasks/${row.original.task_id}/pdf`
          : `workflows/${workflowId}/pdf`;
      const response = await axios.get(
        `${window.config.zensignApiUrl}/share/folders/${folderShareId}/folders/${folderId}/${pdfPath}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      // Supports both filename*=UTF-8''encoded and filename="value" / filename=value
      const starMatch = disposition?.match(/filename\*=(?:UTF-8''|utf-8'')([^;]+)/i);
      const plainMatch = disposition?.match(/filename="?([^";]+)"?/i);
      const rawFilename = starMatch?.[1] || plainMatch?.[1];
      let filename;
      if (rawFilename) {
        try {
          filename = sanitizeFilename(decodeURIComponent(rawFilename.trim()));
        } catch {
          filename = sanitizeFilename(rawFilename.trim());
        }
      }
      filename ??= `${sanitizeFilename(
        keyId && itemName ? `${keyId} ${itemName}` : keyId || itemName || itemType
      )}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Download failed', err);
      dispatch(
        displayToast(formatMessage({ id: 'errors.somethingWentWrong', defaultMessage: 'Something went wrong' }))
      );
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <span style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner size="sm" />
      </span>
    );
  }

  return (
    <span style={{ display: 'flex', justifyContent: 'center' }}>
      <Tooltip
        content={formatMessage({ id: 'folderContentArea.tooltip.download', defaultMessage: 'Download' })}
        position="right center"
        on={['hover', 'focus']}
        trigger={
          <button
            type="button"
            onClick={handleDownload}
            aria-label={`Download ${itemName || 'document'}`}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icon name="cloud-arrow-down" size="md" noHover color="primary-400" />
          </button>
        }
      />
    </span>
  );
}

// ─── Aggregated cell helpers ──────────────────────────────────────────────────

/**
 * Resolve the correct ID and itemType for a row in the aggregated (All Types) view.
 * Extracted so the key_id and name columns share the same logic without duplication.
 */
function resolveAggregatedCellProps(row, getValue, onOpenDocument) {
  const itemType = resolveItemType(row.original.audit_module);
  const id =
    itemType === 'document'
      ? row.original.document_version_id
      : itemType === 'task'
      ? row.original.task_id
      : row.original.item_id;
  const panelTitle =
    row.original.key_id && row.original.name
      ? `${row.original.key_id}: ${row.original.name}`
      : row.original.key_id || row.original.name || '';
  return { value: getValue(), panelTitle, onOpenDocument, documentVersionId: id, itemType };
}

// ─── Column builders ──────────────────────────────────────────────────────────

/**
 * Conditionally prepend a download column to a column array.
 * Centralises the repeated download-column definition used by all view builders.
 */
function withDownloadColumn(columns, downloadContext) {
  if (!downloadContext) return columns;
  return [
    {
      id: 'download',
      header: '',
      size: 64,
      enableSorting: false,
      cell: ({ row }) => <DownloadCell row={row} downloadContext={downloadContext} />,
    },
    ...columns,
  ];
}

/**
 * All Types view (aggregated) — documents + workflow items mixed.
 * Visible: Module icon, Key ID, Name.
 */
export function buildAggregatedColumns(formatMessage, facets = [], onOpenDocument, downloadContext = null) {
  const columns = [
    {
      id: 'audit_module',
      accessorKey: 'audit_module',
      header: formatMessage({ id: 'folderContentArea.column.module', defaultMessage: 'Module' }),
      size: 60,
      enableSorting: false,
      cell: ({ row }) => {
        const mod = row.original.audit_module?.toLowerCase();
        const isDoc = !mod || mod === 'document';
        // Normalize to 'document' when audit_module is absent so that the facet
        // fallback lookup and label resolution receive a defined module string.
        const resolvedMod = isDoc ? 'document' : mod;
        // Prefer the per-row item_type_icon field (matches nova's ModuleCell pattern).
        // Falls back to facet lookup for legacy data that pre-dates the field, then
        // to 'diagram-project' if neither source has an icon.
        const iconName = isDoc
          ? 'file'
          : toZuiIconName(row.original.item_type_icon) ??
            toZuiIconName(facets.find((f) => f.audit_module === resolvedMod)?.icon) ??
            'diagram-project';
        const label = row.original.item_type_name || resolveModuleLabel(resolvedMod, facets);
        return (
          <span style={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip
              content={label}
              position="right center"
              on={['hover', 'focus']}
              trigger={<Icon name={iconName} size="md" noHover color="var(--zui-color-gray-700)" />}
            />
          </span>
        );
      },
    },
    {
      id: 'key_id',
      accessorKey: 'key_id',
      header: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      size: 160,
      cell: ({ getValue, row }) => <DocumentLinkCell {...resolveAggregatedCellProps(row, getValue, onOpenDocument)} />,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: formatMessage({ id: 'folderContentArea.column.name', defaultMessage: 'Name' }),
      size: 500,
      minSize: 280,
      cell: ({ getValue, row }) => <DocumentLinkCell {...resolveAggregatedCellProps(row, getValue, onOpenDocument)} />,
    },
  ];

  return withDownloadColumn(columns, downloadContext);
}

/**
 * Documents view.
 * Visible: Key ID, Document Name, Version, Status, Author, Effective Date, Content Type.
 * Hidden (toggle via ManageColumns): File Type, File Extension.
 * → Pass DOCUMENT_HIDDEN_COLUMNS as initialColumnVisibility to useTable.
 */
export function buildDocumentColumns(formatMessage, onOpenDocument, downloadContext = null) {
  const columns = [
    {
      id: 'key_id',
      accessorKey: 'key_id',
      header: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      size: 160,
      cell: ({ getValue, row }) => (
        <DocumentLinkCell
          value={getValue()}
          panelTitle={
            row.original.key_id && row.original.name
              ? `${row.original.key_id}: ${row.original.name}`
              : row.original.key_id || row.original.name || getValue()
          }
          onOpenDocument={onOpenDocument}
          documentVersionId={row.original.document_version_id}
        />
      ),
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: formatMessage({ id: 'folderContentArea.column.name', defaultMessage: 'Document Name' }),
      size: 500,
      minSize: 280,
      cell: ({ getValue, row }) => (
        <DocumentLinkCell
          value={getValue()}
          panelTitle={
            row.original.key_id && row.original.name
              ? `${row.original.key_id}: ${row.original.name}`
              : row.original.key_id || row.original.name || getValue()
          }
          onOpenDocument={onOpenDocument}
          documentVersionId={row.original.document_version_id}
        />
      ),
    },
    {
      id: 'version',
      accessorKey: 'version',
      header: formatMessage({ id: 'folderContentArea.column.version', defaultMessage: 'Version' }),
      size: 110,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: formatMessage({ id: 'folderContentArea.column.status', defaultMessage: 'Status' }),
      size: 130,
      cell: StatusCell,
    },
    {
      id: 'author_names',
      accessorKey: 'author_names',
      header: formatMessage({ id: 'folderContentArea.column.author', defaultMessage: 'Author' }),
      size: 150,
      enableSorting: false,
    },
    {
      id: 'effective_date',
      accessorKey: 'effective_date',
      header: formatMessage({ id: 'folderContentArea.column.effectiveDate', defaultMessage: 'Effective Date' }),
      size: 160,
      cell: DateCell,
    },
    {
      id: 'content_type',
      accessorKey: 'content_type',
      header: formatMessage({ id: 'folderContentArea.column.contentType', defaultMessage: 'Content Type' }),
      size: 180,
      cell: ContentTypeCell,
    },
    // ── Hidden by default ──────────────────────────────────────────────────────
    {
      id: 'file_type',
      accessorKey: 'file_type',
      header: formatMessage({ id: 'folderContentArea.column.fileType', defaultMessage: 'File Type' }),
      size: 120,
    },
    {
      id: 'file_extension',
      accessorKey: 'file_extension',
      header: formatMessage({ id: 'folderContentArea.column.fileExtension', defaultMessage: 'File Extension' }),
      size: 130,
      cell: UpperCaseCell,
    },
  ];

  return withDownloadColumn(columns, downloadContext);
}

/**
 * Workflows view — issues, change controls, custom workflow types.
 *
 * Backed by the /workflows endpoint which returns the folders_workflows_view shape:
 * key_id, name, current_stage, stage_assignees, stage_due_date, status, audit_module, item_id.
 * Mirrors getWorkflowsColumns / getUnorganizedWorkflowsColumns in the UI workspace.
 */
export function buildWorkflowColumns(formatMessage, onOpenDocument, downloadContext = null) {
  const columns = [
    {
      id: 'key_id',
      accessorKey: 'key_id',
      header: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      size: 160,
      cell: ({ getValue, row }) => (
        <DocumentLinkCell
          value={getValue()}
          panelTitle={
            row.original.key_id && row.original.name
              ? `${row.original.key_id}: ${row.original.name}`
              : row.original.key_id || row.original.name || getValue()
          }
          onOpenDocument={onOpenDocument}
          documentVersionId={row.original.item_id}
          itemType="workflow"
        />
      ),
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: formatMessage({
        id: 'folderContentArea.column.titleShortDescription',
        defaultMessage: 'Title/Short Description',
      }),
      size: 500,
      minSize: 280,
      cell: ({ getValue, row }) => (
        <DocumentLinkCell
          value={getValue()}
          panelTitle={
            row.original.key_id && row.original.name
              ? `${row.original.key_id}: ${row.original.name}`
              : row.original.key_id || row.original.name || getValue()
          }
          onOpenDocument={onOpenDocument}
          documentVersionId={row.original.item_id}
          itemType="workflow"
        />
      ),
    },
    {
      id: 'current_stage',
      accessorKey: 'current_stage',
      header: formatMessage({ id: 'folderContentArea.column.currentStage', defaultMessage: 'Current Stage' }),
      size: 180,
    },
    {
      id: 'stage_assignees',
      accessorKey: 'stage_assignees',
      header: formatMessage({ id: 'folderContentArea.column.stageAssignees', defaultMessage: 'Stage Assignees' }),
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => {
        const assignees = getValue();
        if (!assignees || assignees.length === 0) return null;
        return (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {assignees.map((name, i) => (
              <li key={`${name}-${i}`}>{name}</li>
            ))}
          </ul>
        );
      },
    },
    {
      id: 'stage_due_date',
      accessorKey: 'stage_due_date',
      header: formatMessage({ id: 'folderContentArea.column.stageDueDate', defaultMessage: 'Stage Due Date' }),
      size: 160,
      cell: DateCell,
    },
    // ── Hidden by default (matches UI workspace getWorkflowsColumns) ───────────
    {
      id: 'project_assignees',
      accessorKey: 'project_assignees',
      header: formatMessage({ id: 'folderContentArea.column.projectAssignees', defaultMessage: 'Project Assignees' }),
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => {
        const assignees = getValue();
        if (!assignees || assignees.length === 0) return null;
        return (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {assignees.map((name, i) => (
              <li key={`${name}-${i}`}>{name}</li>
            ))}
          </ul>
        );
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: formatMessage({ id: 'folderContentArea.column.status', defaultMessage: 'Status' }),
      size: 130,
      cell: StatusCell,
    },
  ];

  return withDownloadColumn(columns, downloadContext);
}

// ─── Hidden-column presets (pass as initialColumnVisibility to useTable) ──────

/** Hidden columns for the Documents view. */
export const DOCUMENT_HIDDEN_COLUMNS = { file_type: false, file_extension: false };

/** Hidden columns for the Workflows view — matches UI workspace getWorkflowsColumns hiddenByDefault. */
export const WORKFLOW_HIDDEN_COLUMNS = {
  project_assignees: false,
  status: false,
};

// ─── Filter config builders ───────────────────────────────────────────────────

function buildModuleFilterOptions(facets) {
  return [...facets]
    .sort((a, b) => {
      if (a.audit_module === 'document') return -1;
      if (b.audit_module === 'document') return 1;
      return a.plural_name.localeCompare(b.plural_name, undefined, { sensitivity: 'base' });
    })
    .map((f) => ({ text: resolveModuleLabel(f.audit_module, facets), value: f.audit_module }));
}

/** Filter config for the All Types (aggregated) view. */
export function buildAggregatedFilterColumns(formatMessage, facets = []) {
  const moduleOptions = buildModuleFilterOptions(facets);
  const cols = [
    {
      columnId: 'name',
      label: formatMessage({ id: 'folderContentArea.column.name', defaultMessage: 'Name' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'key_id',
      label: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
  ];
  if (moduleOptions.length > 0) {
    cols.push({
      columnId: 'audit_module',
      label: formatMessage({ id: 'folderContentArea.column.module', defaultMessage: 'Module' }),
      type: 'select',
      operators: ['equal', 'notEqual'],
      options: moduleOptions,
    });
  }
  return cols;
}

/** Filter config for the Documents view. */
export function buildDocumentFilterColumns(formatMessage) {
  return [
    {
      columnId: 'name',
      label: formatMessage({ id: 'folderContentArea.column.name', defaultMessage: 'Document Name' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'key_id',
      label: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'version',
      label: formatMessage({ id: 'folderContentArea.column.version', defaultMessage: 'Version' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'author_names',
      label: formatMessage({ id: 'folderContentArea.column.author', defaultMessage: 'Author' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'status',
      label: formatMessage({ id: 'folderContentArea.column.status', defaultMessage: 'Status' }),
      type: 'select',
      operators: ['equal', 'notEqual'],
      options: [
        { text: 'Approved', value: 'approved' },
        { text: 'Awaiting Approval', value: 'awaiting_approval' },
        { text: 'Draft', value: 'draft' },
        { text: 'Draft Review', value: 'draft_review' },
        { text: 'Effective', value: 'effective' },
        { text: 'Rejected', value: 'rejected' },
        { text: 'Retired', value: 'retired' },
        { text: 'Reviewed', value: 'reviewed' },
      ],
    },
    {
      columnId: 'effective_date',
      label: formatMessage({ id: 'folderContentArea.column.effectiveDate', defaultMessage: 'Effective Date' }),
      type: 'date',
      operators: ['equal', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    },
    {
      columnId: 'content_type',
      label: formatMessage({ id: 'folderContentArea.column.contentType', defaultMessage: 'Content Type' }),
      type: 'select',
      operators: ['equal', 'notEqual'],
      options: [
        { text: 'Document', value: 'document' },
        { text: 'SCORM', value: 'scorm' },
      ],
    },
    {
      columnId: 'file_type',
      label: formatMessage({ id: 'folderContentArea.column.fileType', defaultMessage: 'File Type' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'file_extension',
      label: formatMessage({ id: 'folderContentArea.column.fileExtension', defaultMessage: 'File Extension' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
  ];
}

/** Filter config for the Workflows view — fields from folders_workflows_view. */
export function buildWorkflowFilterColumns(formatMessage) {
  return [
    {
      columnId: 'name',
      label: formatMessage({
        id: 'folderContentArea.column.titleShortDescription',
        defaultMessage: 'Title/Short Description',
      }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'key_id',
      label: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'current_stage',
      label: formatMessage({ id: 'folderContentArea.column.currentStage', defaultMessage: 'Current Stage' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'stage_assignees',
      label: formatMessage({ id: 'folderContentArea.column.stageAssignees', defaultMessage: 'Stage Assignees' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'stage_due_date',
      label: formatMessage({ id: 'folderContentArea.column.stageDueDate', defaultMessage: 'Stage Due Date' }),
      type: 'date',
      operators: ['equal', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    },
    {
      columnId: 'project_assignees',
      label: formatMessage({ id: 'folderContentArea.column.projectAssignees', defaultMessage: 'Project Assignees' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'status',
      label: formatMessage({ id: 'folderContentArea.column.status', defaultMessage: 'Status' }),
      type: 'select',
      operators: ['equal', 'notEqual'],
      options: [
        { text: 'Open', value: 'Open' },
        { text: 'Closed', value: 'Closed' },
      ],
    },
  ];
}

/**
 * Tasks view — backed by /tasks endpoint (folders_tasks_view shape).
 * key_id, name, task_type_name, assignees, due_date, status, task_id.
 */
export function buildTaskColumns(formatMessage, onOpenDocument, downloadContext = null) {
  const columns = [
    {
      id: 'key_id',
      accessorKey: 'key_id',
      header: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      size: 160,
      cell: ({ getValue, row }) => (
        <DocumentLinkCell
          value={getValue()}
          panelTitle={
            row.original.key_id && row.original.name
              ? `${row.original.key_id}: ${row.original.name}`
              : row.original.key_id || row.original.name || getValue()
          }
          onOpenDocument={onOpenDocument}
          documentVersionId={row.original.task_id}
          itemType="task"
        />
      ),
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: formatMessage({ id: 'folderContentArea.column.taskName', defaultMessage: 'Title/Short Description' }),
      size: 400,
      minSize: 200,
      cell: ({ getValue, row }) => (
        <DocumentLinkCell
          value={getValue()}
          panelTitle={
            row.original.key_id && row.original.name
              ? `${row.original.key_id}: ${row.original.name}`
              : row.original.key_id || row.original.name || getValue()
          }
          onOpenDocument={onOpenDocument}
          documentVersionId={row.original.task_id}
          itemType="task"
        />
      ),
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: formatMessage({ id: 'folderContentArea.column.status', defaultMessage: 'Status' }),
      size: 130,
      cell: StatusCell,
    },
    {
      id: 'due_date',
      accessorKey: 'due_date',
      header: formatMessage({ id: 'folderContentArea.column.dueDate', defaultMessage: 'Due Date' }),
      size: 150,
      cell: DateCell,
    },
    {
      id: 'assignees',
      accessorKey: 'assignees',
      header: formatMessage({ id: 'folderContentArea.column.assignees', defaultMessage: 'Task Assignees' }),
      size: 180,
      enableSorting: false,
      cell: ({ getValue }) => {
        const assignees = getValue();
        if (!assignees || assignees.length === 0) return null;
        return (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {assignees.map((name, i) => (
              <li key={`${name}-${i}`}>{name}</li>
            ))}
          </ul>
        );
      },
    },
    // ── Hidden by default ──────────────────────────────────────────────────────
    {
      id: 'completed_at',
      accessorKey: 'completed_at',
      header: formatMessage({ id: 'folderContentArea.column.completionDate', defaultMessage: 'Completion Date' }),
      size: 150,
      cell: DateCell,
    },
    {
      id: 'details',
      accessorKey: 'details',
      header: formatMessage({ id: 'folderContentArea.column.taskDetails', defaultMessage: 'Task Details' }),
      size: 300,
      enableSorting: false,
      cell: ({ getValue }) => {
        const v = getValue();
        if (!v) return null;
        // Task Details is authored as rich text (bold, columns, line breaks, etc. —
        // see foldersTableConfig.tsx's TaskDetailsCell in the UI workspace, which
        // renders the same field as HTML). Sanitize with DOMPurify before rendering
        // so formatting is preserved while staying safe against injected <script>
        // tags or nested-tag bypass patterns (CodeQL CWE-116) — DOMPurify's parser
        // walks the resulting DOM tree rather than pattern-matching tag text, so it
        // isn't fooled by constructs like <scr<script>ipt>.
        const sanitizedHtml = DOMPurify.sanitize(String(v));
        return <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
      },
    },
  ];

  return withDownloadColumn(columns, downloadContext);
}

/** Hidden columns for the Tasks view — matches UI workspace getTasksColumns hiddenByDefault. */
export const TASK_HIDDEN_COLUMNS = {
  completed_at: false,
  details: false,
};

/** Filter config for the Tasks view. */
export function buildTaskFilterColumns(formatMessage) {
  return [
    {
      columnId: 'name',
      label: formatMessage({ id: 'folderContentArea.column.name', defaultMessage: 'Task Name' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'key_id',
      label: formatMessage({ id: 'folderContentArea.column.keyId', defaultMessage: 'Key ID' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'status',
      label: formatMessage({ id: 'folderContentArea.column.status', defaultMessage: 'Status' }),
      type: 'select',
      operators: ['equal', 'notEqual'],
      options: [
        { text: 'Active', value: 'active' },
        { text: 'Completed', value: 'completed' },
        { text: 'Draft', value: 'draft' },
        { text: 'Not Implemented', value: 'not_implemented' },
        { text: 'Rejected', value: 'rejected' },
      ],
    },
    {
      columnId: 'due_date',
      label: formatMessage({ id: 'folderContentArea.column.dueDate', defaultMessage: 'Due Date' }),
      type: 'date',
      operators: ['equal', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    },
    {
      columnId: 'assignees',
      label: formatMessage({ id: 'folderContentArea.column.assignees', defaultMessage: 'Task Assignees' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
    {
      columnId: 'completed_at',
      label: formatMessage({ id: 'folderContentArea.column.completionDate', defaultMessage: 'Completion Date' }),
      type: 'date',
      operators: ['equal', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual'],
    },
    {
      columnId: 'details',
      label: formatMessage({ id: 'folderContentArea.column.taskDetails', defaultMessage: 'Task Details' }),
      type: 'text',
      operators: ['contains', 'notContains', 'equal', 'notEqual', 'startsWith', 'endsWith'],
    },
  ];
}
